'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from './DrawingToolbar.module.css'

export type DrawingToolType = 
  | 'select'
  | 'trendline' 
  | 'horizontal'
  | 'vertical'
  | 'fibonacci-retracement'
  | 'fibonacci-extension'
  | 'channel'
  | 'parallel-channel'
  | 'rectangle'
  | 'text'
  | 'head-and-shoulders'
  | 'double-top'
  | 'double-bottom'
  | 'ascending-triangle'
  | 'descending-triangle'
  | 'symmetrical-triangle'
  | 'rising-wedge'
  | 'falling-wedge'
  | 'bull-flag'
  | 'bear-flag'
  | 'pennant'

export interface DrawingTool {
  id: DrawingToolType
  name: string
  icon: string
  category: 'basic' | 'fibonacci' | 'channels' | 'shapes' | 'patterns'
  description: string
}

const DRAWING_TOOLS: DrawingTool[] = [
  // Basic Tools
  {
    id: 'select',
    name: 'Select',
    icon: '↗️',
    category: 'basic',
    description: 'Select and edit existing drawings'
  },
  {
    id: 'trendline',
    name: 'Trend Line',
    icon: '📈',
    category: 'basic',
    description: 'Draw trend lines to identify support and resistance'
  },
  {
    id: 'horizontal',
    name: 'Horizontal Line',
    icon: '➡️',
    category: 'basic',
    description: 'Draw horizontal support/resistance lines'
  },
  {
    id: 'vertical',
    name: 'Vertical Line',
    icon: '⬇️',
    category: 'basic',
    description: 'Draw vertical time lines'
  },
  
  // Fibonacci Tools
  {
    id: 'fibonacci-retracement',
    name: 'Fibonacci Retracement',
    icon: '🌀',
    category: 'fibonacci',
    description: 'Identify potential support/resistance levels using Fib ratios (23.6%, 38.2%, 50%, 61.8%)'
  },
  {
    id: 'fibonacci-extension',
    name: 'Fibonacci Extension',
    icon: '🌀⬆️',
    category: 'fibonacci',
    description: 'Project price targets using Fib extensions (127.2%, 161.8%, 261.8%)'
  },
  
  // Channel Tools
  {
    id: 'channel',
    name: 'Parallel Channel',
    icon: '📊',
    category: 'channels',
    description: 'Draw parallel channels to identify trend boundaries'
  },
  {
    id: 'parallel-channel',
    name: 'Linear Regression Channel',
    icon: '📈📊',
    category: 'channels',
    description: 'Draw regression-based channels'
  },
  
  // Shape Tools
  {
    id: 'rectangle',
    name: 'Rectangle',
    icon: '⬜',
    category: 'shapes',
    description: 'Draw rectangles to mark consolidation zones'
  },
  {
    id: 'text',
    name: 'Text Note',
    icon: '💬',
    category: 'shapes',
    description: 'Add text annotations to chart'
  },

  // Pattern Recognition Tools
  {
    id: 'head-and-shoulders',
    name: 'Head & Shoulders',
    icon: '👤',
    category: 'patterns',
    description: 'Identify Head & Shoulders reversal pattern with left shoulder, head, right shoulder, and neckline'
  },
  {
    id: 'double-top',
    name: 'Double Top',
    icon: '⛰️⛰️',
    category: 'patterns',
    description: 'Mark double top bearish reversal pattern with two peaks at similar levels'
  },
  {
    id: 'double-bottom',
    name: 'Double Bottom',
    icon: '🕳️🕳️',
    category: 'patterns',
    description: 'Mark double bottom bullish reversal pattern with two lows at similar levels'
  },
  {
    id: 'ascending-triangle',
    name: 'Ascending Triangle',
    icon: '📊▲',
    category: 'patterns',
    description: 'Bullish continuation pattern with horizontal resistance and rising support'
  },
  {
    id: 'descending-triangle',
    name: 'Descending Triangle',
    icon: '📊▼',
    category: 'patterns',
    description: 'Bearish continuation pattern with horizontal support and declining resistance'
  },
  {
    id: 'symmetrical-triangle',
    name: 'Symmetrical Triangle',
    icon: '📊◆',
    category: 'patterns',
    description: 'Continuation pattern with converging support and resistance lines'
  },
  {
    id: 'rising-wedge',
    name: 'Rising Wedge',
    icon: '⬆️📐',
    category: 'patterns',
    description: 'Bearish reversal pattern with converging upward sloping lines'
  },
  {
    id: 'falling-wedge',
    name: 'Falling Wedge',
    icon: '⬇️📐',
    category: 'patterns',
    description: 'Bullish reversal pattern with converging downward sloping lines'
  },
  {
    id: 'bull-flag',
    name: 'Bull Flag',
    icon: '🚩📈',
    category: 'patterns',
    description: 'Bullish continuation pattern with strong upward move followed by consolidation'
  },
  {
    id: 'bear-flag',
    name: 'Bear Flag',
    icon: '🚩📉',
    category: 'patterns',
    description: 'Bearish continuation pattern with strong downward move followed by consolidation'
  },
  {
    id: 'pennant',
    name: 'Pennant',
    icon: '🏁',
    category: 'patterns',
    description: 'Continuation pattern with triangular consolidation after strong directional move'
  }
]

interface DrawingToolbarProps {
  activeTool: DrawingToolType
  onToolSelect: (tool: DrawingToolType) => void
  onClearAll: () => void
  onUndo: () => void
  onRedo: () => void
  canUndo: boolean
  canRedo: boolean
  onAutoDetectPatterns: () => void
  onAIAnalysis: () => void
}

const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  activeTool,
  onToolSelect,
  onClearAll,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAutoDetectPatterns,
  onAIAnalysis
}) => {
  const [showPopup, setShowPopup] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [mounted, setMounted] = useState(false)

  // Set mounted state to true after component mounts for portal rendering
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const categories = [
    { id: 'all', name: 'All Tools', icon: '🔧' },
    { id: 'basic', name: 'Basic', icon: '📏' },
    { id: 'fibonacci', name: 'Fibonacci', icon: '🌀' },
    { id: 'channels', name: 'Channels', icon: '📊' },
    { id: 'shapes', name: 'Shapes', icon: '⬜' },
    { id: 'patterns', name: 'Patterns', icon: '🏁' }
  ]

  const filteredTools = selectedCategory === 'all' 
    ? DRAWING_TOOLS 
    : DRAWING_TOOLS.filter(tool => tool.category === selectedCategory)

  const handleToolSelect = useCallback((tool: DrawingToolType) => {
    onToolSelect(tool)
    setShowPopup(false)
  }, [onToolSelect])

  const activeToolInfo = DRAWING_TOOLS.find(tool => tool.id === activeTool)

  return (
    <div className={styles.toolbar}>
      {/* Current Active Tool */}
      <div className={styles.activeToolSection}>
        <button 
          className={`${styles.activeToolButton} ${activeTool !== 'select' ? styles.active : ''}`}
          onClick={() => setShowPopup(!showPopup)}
          title={activeToolInfo?.description || 'Select drawing tool'}
        >
          <span className={styles.toolIcon}>{activeToolInfo?.icon || '↗️'}</span>
          <span className={styles.toolName}>{activeToolInfo?.name || 'Select'}</span>
          <span className={styles.dropdownArrow}>▼</span>
        </button>
      </div>

      {/* Action Buttons */}
      <div className={styles.actionButtons}>
        <button 
          className={styles.actionButton}
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo last drawing (Ctrl+Z)"
        >
          ↶
        </button>
        <button 
          className={styles.actionButton}
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo last drawing (Ctrl+Y)"
        >
          ↷
        </button>
        <button 
          className={styles.actionButton}
          onClick={onClearAll}
          title="Clear all drawings"
        >
          🗑️
        </button>
        <button 
          className={`${styles.actionButton} ${styles.patternDetectionButton}`}
          onClick={onAutoDetectPatterns}
          title="Auto-detect chart patterns"
        >
          🔍
        </button>
        <button 
          className={`${styles.actionButton} ${styles.aiAnalysisButton}`}
          onClick={onAIAnalysis}
          title="AI Chart Analysis - Get trading insights powered by GPT-4o"
        >
          🤖
        </button>
      </div>

      {/* Drawing Tools Modal - rendered as portal in center of screen */}
      {showPopup && mounted && createPortal(
        <div 
          className={styles.modalOverlay}
          onClick={() => setShowPopup(false)}
        >
          <div 
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.popupHeader}>
              <h3>Drawing Tools</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowPopup(false)}
              >
                ✕
              </button>
            </div>

            {/* Category Tabs */}
            <div className={styles.categoryTabs}>
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`${styles.categoryTab} ${selectedCategory === category.id ? styles.activeCategory : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <span className={styles.categoryIcon}>{category.icon}</span>
                  <span className={styles.categoryName}>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Tools Grid */}
            <div className={styles.toolsGrid}>
              {filteredTools.map(tool => (
                <button
                  key={tool.id}
                  className={`${styles.toolButton} ${activeTool === tool.id ? styles.selectedTool : ''}`}
                  onClick={() => handleToolSelect(tool.id)}
                  title={tool.description}
                >
                  <span className={styles.toolIcon}>{tool.icon}</span>
                  <span className={styles.toolLabel}>{tool.name}</span>
                </button>
              ))}
            </div>

            {/* Quick Access for Popular Tools */}
            <div className={styles.quickAccess}>
              <h4>🔥 Most Popular</h4>
              <div className={styles.quickToolsRow}>
                {['trendline', 'fibonacci-retracement', 'channel', 'horizontal'].map(toolId => {
                  const tool = DRAWING_TOOLS.find(t => t.id === toolId)
                  return tool ? (
                    <button
                      key={tool.id}
                      className={styles.quickTool}
                      onClick={() => handleToolSelect(tool.id as DrawingToolType)}
                      title={tool.description}
                    >
                      {tool.icon} {tool.name}
                    </button>
                  ) : null
                })}
              </div>
            </div>

            {/* Keyboard Shortcuts */}
            <div className={styles.shortcuts}>
              <h4>⌨️ Shortcuts</h4>
              <div className={styles.shortcutsList}>
                <div><kbd>T</kbd> Trend Line</div>
                <div><kbd>H</kbd> Horizontal Line</div>
                <div><kbd>F</kbd> Fibonacci</div>
                <div><kbd>C</kbd> Channel</div>
                <div><kbd>Esc</kbd> Select Tool</div>
                <div><kbd>Del</kbd> Delete Selected</div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default DrawingToolbar 